/**
 * useActionChips — Resolves actions.json into live trigger chip arrays.
 *
 * Reads context-specific button definitions from actions.json, applies:
 *   1. Context detection (patientDetail / documentEditor / dashboard / patientsList / generic)
 *   2. Feature gate filtering (three-layer resolveFeatureFlag)
 *   3. Conditional visibility (showWhen: 'hasUnsavedChanges', etc.)
 *   4. State-aware swaps (isRecording → Stop/Pause, isProcessing → View Progress)
 *   5. Usage-based ordering (most-used first, primary emphasis on top chip)
 *   6. Icon resolution (string name → lucide-react component)
 *
 * Returns { chips, chatPresets } ready for TriggerChips and InputRow.
 *
 * See: docs/features/infrastructure/activity-bar-redesign.md
 */
import { useMemo } from "react";
import * as LucideIcons from "lucide-react";
import actionsConfig from "../config/activityBar/actions.json";

/**
 * Resolve an icon name string (from JSON) to a lucide-react component.
 * Returns null for unrecognized names — TriggerChips handles missing icons gracefully.
 */
function resolveIcon(iconName) {
    if (!iconName) return null;
    return LucideIcons[iconName] || null;
}

/**
 * @param {Object} options
 * @param {string} options.currentContext - 'patientDetail' | 'documentEditor' | 'dashboard' | null
 * @param {Object} options.stateFlags - Runtime state: { isRecording, isProcessing, hasUnsavedChanges }
 * @param {Object} options.actionUsage - Usage counts map from user settings: { [actionId]: number }
 * @param {Function} options.resolveFeatureFlag - Feature flag resolver (optional — if null, all buttons shown)
 * @param {Object} options.featureGateContext - { systemConfig, companyDoc, userSettings } for resolver
 * @returns {{ chips: Array, chatPresets: Array }}
 */
export function useActionChips({
    currentContext,
    stateFlags = {},
    actionUsage = {},
    resolveFeatureFlag: resolveFlag = null,
    featureGateContext = {},
} = {}) {
    const { isRecording = false, isProcessing = false, hasUnsavedChanges = false, profileIncomplete = false } = stateFlags;

    const result = useMemo(() => {
        if (!currentContext || !actionsConfig[currentContext]) {
            return { chips: [], chatPresets: [] };
        }

        const contextDef = actionsConfig[currentContext];
        const stateMap = { isRecording, isProcessing, hasUnsavedChanges, profileIncomplete };

        // ── Step 1: Filter buttons by feature gate and conditional visibility ──
        let buttons = (contextDef.buttons || []).filter((btn) => {
            // Feature gate check
            if (btn.featureFlag && resolveFlag) {
                const { enabled } = resolveFlag(btn.featureFlag, featureGateContext);
                if (!enabled) return false;
            }

            // Conditional visibility (showWhen)
            if (btn.showWhen && !stateMap[btn.showWhen]) return false;

            return true;
        });

        // ── Step 2: Apply state-aware swaps ──
        buttons = buttons.flatMap((btn) => {
            if (btn.stateSwap && stateMap[btn.stateSwap.trigger]) {
                // Swap: replace this button with its swap buttons
                return (btn.stateSwap.buttons || []).map((swapBtn) => ({
                    ...swapBtn,
                    actionType: btn.actionType,
                    featureFlag: btn.featureFlag,
                    _swappedFrom: btn.id,
                }));
            }
            return [btn];
        });

        // ── Step 3: Sort by usage frequency (descending) ──
        buttons.sort((a, b) => {
            const usageA = actionUsage[a.id] || actionUsage[a._swappedFrom] || 0;
            const usageB = actionUsage[b.id] || actionUsage[b._swappedFrom] || 0;
            return usageB - usageA;
        });

        // ── Step 4: Resolve icons and build chip objects ──
        const chips = buttons.map((btn, index) => ({
            id: btn.id,
            label: btn.label,
            icon: resolveIcon(btn.icon),
            actionType: btn.actionType,
            handler: btn.handler,
            variant: btn.variant || null,
            primary: index === 0, // Most-used gets primary emphasis
        }));

        // ── Step 5: Build chat presets (no swaps, no feature gates for now) ──
        const chatPresets = (contextDef.chatPresets || []).map((preset) => ({
            id: preset.id,
            label: preset.label,
            chatFunction: preset.chatFunction || null,
            handler: preset.handler || null,
            params: preset.params || {},
        }));

        return { chips, chatPresets };
    }, [
        currentContext,
        isRecording,
        isProcessing,
        hasUnsavedChanges,
        profileIncomplete,
        actionUsage,
        resolveFlag,
        featureGateContext,
    ]);

    return result;
}
